import { useCallback, useEffect, useState } from 'react'
import { Card } from '@components/primitives/Card.js'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type Platform = 'ios' | 'android' | 'other'

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'other'
}

function InstallGuideSteps({ platform }: { platform: Platform }) {
  if (platform === 'ios') {
    return (
      <ol className='cf3-install-steps'>
        <li>
          Toque no botão <strong>Compartilhar</strong> (quadrado com seta para cima), na barra
          inferior do Safari.
        </li>
        <li>
          Role e toque em <strong>Adicionar à Tela de Início</strong>.
        </li>
        <li>
          Confirme o nome <strong>Minha Obra</strong> e toque em <strong>Adicionar</strong>.
        </li>
        <li>Abra o app pelo ícone na tela inicial · não pela aba do Safari.</li>
      </ol>
    )
  }

  if (platform === 'android') {
    return (
      <ol className='cf3-install-steps'>
        <li>
          Abra o menu <strong>⋮</strong> do Chrome ou Brave.
        </li>
        <li>
          Toque em <strong>Instalar app</strong> ou <strong>Adicionar à tela inicial</strong>.
        </li>
        <li>
          Confirme em <strong>Instalar</strong> ou <strong>Adicionar</strong>.
        </li>
        <li>
          O ícone <strong>Minha Obra</strong> aparece na tela inicial. Abra sempre por esse ícone.
        </li>
      </ol>
    )
  }

  return (
    <ol className='cf3-install-steps'>
      <li>
        No Chrome: menu → <strong>Instalar Minha Obra…</strong>
      </li>
      <li>Confirme a instalação.</li>
      <li>Seus dados continuam salvos neste aparelho.</li>
    </ol>
  )
}

function installGuideTitle(platform: Platform): string {
  if (platform === 'ios') return 'Adicionar à Tela de Início'
  if (platform === 'android') return 'Instalar no Android'
  return 'Instalar no computador'
}

function installGuideIntro(platform: Platform): string {
  if (platform === 'ios') {
    return 'No iPhone e iPad, o Safari não permite instalação com um toque. Siga estes passos:'
  }
  if (platform === 'android') {
    return 'Seu navegador não abriu a instalação automática. Siga estes passos no menu do Chrome ou Brave:'
  }
  return 'Use o menu do navegador para instalar:'
}

export function InstallAppSection() {
  const [installed, setInstalled] = useState(isStandalone)
  const [platform, setPlatform] = useState(detectPlatform)
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [msg, setMsg] = useState('')
  const [showGuide, setShowGuide] = useState(false)

  useEffect(() => {
    setInstalled(isStandalone())
    setPlatform(detectPlatform())

    const onBip = (e: Event) => {
      e.preventDefault()
      setPromptEvent(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setPromptEvent(null)
      setShowGuide(false)
    }

    window.addEventListener('beforeinstallprompt', onBip)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const instalar = useCallback(async () => {
    if (promptEvent) {
      await promptEvent.prompt()
      const { outcome } = await promptEvent.userChoice
      setPromptEvent(null)
      setMsg(outcome === 'accepted' ? 'App instalado! Procure o ícone na tela inicial.' : '')
      return
    }

    setShowGuide(true)
  }, [promptEvent])

  if (installed) {
    return (
      <Card surface='raised'>
        <div className='cf3-config-section'>
          <p className='cf3-config-section__title'>App instalado</p>
          <p className='cf3-config-section__desc'>
            Você está usando Minha Obra como aplicativo. Seus dados ficam salvos neste dispositivo.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card surface='raised'>
        <div className='cf3-config-section'>
          <p className='cf3-config-section__title'>Instalar no celular</p>
          <p className='cf3-config-section__desc'>
            Instale para abrir em tela cheia, como um app · sem barra de endereço.
          </p>

          <div className='cf3-install-actions'>
            <Button variant='primary' onClick={instalar}>
              Instalar aplicativo
            </Button>
            <p className='cf3-install-hint'>
              {promptEvent
                ? 'Toque para instalar o aplicativo.'
                : platform === 'ios'
                  ? 'Toque para ver como adicionar à tela inicial.'
                  : 'Toque para instalar ou ver o passo a passo.'}
            </p>
          </div>

          {msg && <p className='cf3-install-hint cf3-install-hint--ok'>{msg}</p>}

          <p className='cf3-install-hint'>
            <strong>Salvar dados:</strong> use <em>Exportar backup</em> abaixo com frequência. Tudo
            continua guardado neste aparelho.
          </p>
        </div>
      </Card>

      <Modal
        open={showGuide}
        title={installGuideTitle(platform)}
        onClose={() => setShowGuide(false)}
        actions={
          <Button variant='primary' onClick={() => setShowGuide(false)}>
            Entendi
          </Button>
        }
      >
        <p className='cf3-config-section__desc'>{installGuideIntro(platform)}</p>
        <InstallGuideSteps platform={platform} />
      </Modal>
    </>
  )
}
